import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DealerRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company_name: "",
    company_address: "", country: "", eu_vat_number: "", password: "",
    // Phase 1 extended KYC fields
    company_registration_number: "", company_website: "",
    representative_full_name: "", representative_position: "",
    bank_iban: "", vat_country: "",
  });
  const [document, setDocument] = useState(null);
  const [documentExtra, setDocumentExtra] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!document) { toast.error("Kérjük, töltsön fel egy céges dokumentumot!"); return; }
    setLoading(true);
    try {
      // 1. Register
      const res = await axios.post(`${BACKEND_URL}/api/dealer/register`, form);
      const dealerId = res.data.id;

      // 2. Upload primary document
      const fd = new FormData();
      fd.append("file", document);
      await axios.post(`${BACKEND_URL}/api/dealer/upload-document?dealer_id=${dealerId}&slot=primary`, fd);

      // 2b. Upload optional extra document
      if (documentExtra) {
        const fdExtra = new FormData();
        fdExtra.append("file", documentExtra);
        await axios.post(`${BACKEND_URL}/api/dealer/upload-document?dealer_id=${dealerId}&slot=extra`, fdExtra);
      }

      toast.success("Sikeres regisztráció! Az admin hamarosan jóváhagyja a fiókját.");
      setTimeout(() => navigate("/dealer-login"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba történt a regisztráció során");
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { name: "full_name", label: "Teljes név", type: "text", placeholder: "Kovács János" },
    { name: "email", label: "Email cím", type: "email", placeholder: "kereskedő@ceg.hu" },
    { name: "phone", label: "Telefonszám", type: "tel", placeholder: "+36 30 123 4567" },
    { name: "company_name", label: "Cégnév", type: "text", placeholder: "Auto Kft." },
    { name: "company_registration_number", label: "Cégjegyzékszám", type: "text", placeholder: "01-09-XXXXXX", optional: true },
    { name: "company_website", label: "Céges weboldal", type: "url", placeholder: "https://autokft.hu", optional: true },
    { name: "company_address", label: "Cég címe", type: "text", placeholder: "Budapest, Fő utca 1." },
    { name: "country", label: "Ország", type: "text", placeholder: "Magyarország" },
    { name: "vat_country", label: "ÁFA ország", type: "text", placeholder: "HU", optional: true },
    { name: "eu_vat_number", label: "EU ÁFA szám", type: "text", placeholder: "HU12345678" },
    { name: "representative_full_name", label: "Képviselő neve", type: "text", placeholder: "Kovács János", optional: true },
    { name: "representative_position", label: "Képviselő beosztása", type: "text", placeholder: "Ügyvezető", optional: true },
    { name: "bank_iban", label: "Bankszámla / IBAN", type: "text", placeholder: "HU00 0000 0000 0000 0000 0000 0000", optional: true },
    { name: "password", label: "Jelszó", type: "password", placeholder: "Minimum 6 karakter" },
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/alpine-car-logo.png" alt="Alpine Car" className="w-10 h-10 rounded-lg object-cover" />
            <span className="font-bold text-lg">Alpine Car</span>
          </Link>
          <Link to="/dealer-login" className="text-sm text-[#D4AF37] hover:underline" data-testid="login-link">
            Bejelentkezés
          </Link>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-4">
            <svg className="w-4 h-4 text-[#D4AF37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span className="text-xs text-[#D4AF37] font-medium">Kereskedői hozzáférés</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Kereskedői regisztráció</h1>
          <p className="text-white/50 text-sm">Regisztráljon kereskedőként a licit rendszerhez való hozzáférésért</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="dealer-register-form">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-xs text-white/60 mb-1.5">{f.label}{f.optional ? "" : " *"}</label>
              <input name={f.name} type={f.type} required={!f.optional} value={form[f.name]} onChange={handleChange}
                placeholder={f.placeholder} minLength={f.name === "password" ? 6 : undefined}
                className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors"
                data-testid={`input-${f.name}`}
              />
            </div>
          ))}

          {/* Document upload */}
          <div>
            <label className="block text-xs text-white/60 mb-1.5">Céges dokumentum (cégkivonat, igazolvány) *</label>
            <div className="relative">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setDocument(e.target.files[0])}
                className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-[#D4AF37]/10 file:text-[#D4AF37] hover:file:bg-[#D4AF37]/20"
                data-testid="input-document"
              />
            </div>
            {document && <p className="text-xs text-green-400 mt-1">{document.name}</p>}
          </div>

          {/* Optional extra document upload */}
          <div>
            <label className="block text-xs text-white/60 mb-1.5">Kiegészítő dokumentum (opcionális)</label>
            <div className="relative">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setDocumentExtra(e.target.files[0])}
                className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-[#D4AF37]/10 file:text-[#D4AF37] hover:file:bg-[#D4AF37]/20"
                data-testid="input-document-extra"
              />
            </div>
            {documentExtra && <p className="text-xs text-green-400 mt-1">{documentExtra.name}</p>}
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-[#D4AF37] text-black font-semibold py-3.5 rounded-lg hover:bg-[#c4a030] disabled:opacity-50 transition-colors mt-6"
            data-testid="submit-register">
            {loading ? "Regisztráció folyamatban..." : "Regisztráció"}
          </button>
        </form>

        <p className="text-center text-sm text-white/40 mt-6">
          Már van fiókja? <Link to="/dealer-login" className="text-[#D4AF37] hover:underline">Bejelentkezés</Link>
        </p>
      </div>
    </div>
  );
}
